// @ts-strict-ignore
import { createSelector } from 'reselect';

import { Platform } from 'constants/app-platform';
import { Permission } from 'constants/permission';
import { getAppPlatform } from 'helpers/browser';
import { shouldHaveMobileOnboardingSteps } from 'helpers/device';
import { PartnerType } from 'interfaces/entities/current-license';
import { InvitationMode } from 'store/entities/accounts/interfaces';
import { getInvitationMode } from 'store/entities/accounts/selectors';
import { getActiveAgentsCount, getLoggedInAgent } from 'store/entities/agents/selectors';
import { type IApplication } from 'store/entities/applications/interfaces';
import { getInstalledIntegrations } from 'store/entities/applications/selectors';
import { getOnboardingIntegrations } from 'store/entities/integration-license-properties/selectors';
import { getPaymentErrorOnAccessBlockedForLicense } from 'store/entities/license-custom-properties/selectors';
import { getSubscriptionSeats } from 'store/entities/subscription/selectors';
import {
  getPaymentErrorOnAccessBlockedForAgent,
  getUtmSource,
  isDesktopAppDisallowedByLicenseSource,
  type IWithAgentCustomPropertiesState,
} from 'store/features/agent-custom-properties/selectors';
import {
  getCanManageSubscription,
  getIsEnterprisePlan,
  getIsOnTrial,
  getIsOneSeatStarter,
  getIsPPSBusinessModel,
  getPartnerType,
} from 'store/features/session/selectors';

import { supportedIntegrations } from './constants';
import { prepareOnboardingSteps } from './helpers';
import type { IOnboardingState } from './interfaces';

interface WithOnboardingState {
  views: {
    onboarding: IOnboardingState;
  };
}

export const getIsSupportedIntegration = (state: IWithAgentCustomPropertiesState): boolean =>
  supportedIntegrations.includes(getUtmSource(state));

export const getIsOnboardingFinished = (state: WithOnboardingState): boolean =>
  state.views.onboarding.isOnboardingFinished;

export const getIsShowingInviteDeveloperForm = (state: WithOnboardingState): boolean =>
  state.views.onboarding.isShowingInviteDeveloperForm;

const getIsCompanySizeDetected = (state: WithOnboardingState): boolean => state.views.onboarding.isCompanySizeDetected;

const getIsInstallationStepSkipped = (state: WithOnboardingState): boolean =>
  state.views.onboarding.isInstallationStepSkipped;

export const getIsRefetchTechnologies = (state: WithOnboardingState): boolean =>
  state.views.onboarding.isRefetchTechnologies;

let initialIntegrations: IApplication[] | string | null = null;

export const getSteps = createSelector(
  getIsSupportedIntegration,
  getLoggedInAgent,
  isDesktopAppDisallowedByLicenseSource,
  getIsPPSBusinessModel,
  getIsCompanySizeDetected,
  getIsInstallationStepSkipped,
  getIsOneSeatStarter,
  getPaymentErrorOnAccessBlockedForLicense,
  getPaymentErrorOnAccessBlockedForAgent,
  getInstalledIntegrations,
  getOnboardingIntegrations,
  getIsEnterprisePlan,
  getIsOnTrial,
  getPartnerType,
  getSubscriptionSeats,
  getActiveAgentsCount,
  getInvitationMode,
  getCanManageSubscription,
  () => location.pathname,
  (
    isSupportedIntegration,
    loggedInUser,
    isDesktopAppDisallowed,
    isPPS,
    isCompanySizeDetected,
    isInstallationStepSkipped,
    isOneSeatStarter,
    paymentErrorOnAccessBlocked,
    paymentErrorOnAccessBlockedForAgent,
    installedIntegrations,
    onboardingIntegrations,
    isEnterprisePlan,
    isOnTrial,
    partnerType,
    subscriptionSeats,
    activeAgentsCount,
    invitationMode,
    canManageSubscription,
    currentRoute,
  ) => {
    if (initialIntegrations === null) {
      initialIntegrations = installedIntegrations || onboardingIntegrations;
    }

    const platform = getAppPlatform();
    const isSupportedPlatform = platform === Platform.Mac || platform === Platform.Windows;

    const isOwner = loggedInUser.permission === Permission.Owner;
    const isAgent = loggedInUser.permission === Permission.Normal;
    const isPaymentError = paymentErrorOnAccessBlocked || paymentErrorOnAccessBlockedForAgent;

    const includeIntegrationsStep =
      !isAgent && !isSupportedIntegration && (initialIntegrations.length === 0 || initialIntegrations === '');

    const includeDownloadDesktopAppStep =
      isSupportedIntegration && !shouldHaveMobileOnboardingSteps() && !isDesktopAppDisallowed && isSupportedPlatform;

    const includeDownloadMobileAppStep = shouldHaveMobileOnboardingSteps();

    const includeDownloadStep = includeDownloadDesktopAppStep || includeDownloadMobileAppStep;

    const includeInstallStep = !isSupportedIntegration && !isInstallationStepSkipped;

    // checks for paid enterprise subscription
    const isReseller = partnerType === PartnerType.Reseller;
    const isEnterpriseSeatLimitApplicable = isEnterprisePlan && !isReseller && !isOnTrial;
    const freeSeats = typeof subscriptionSeats === 'number' ? subscriptionSeats - activeAgentsCount : 0;
    const isSeatsFullForEnterprise = isEnterpriseSeatLimitApplicable && freeSeats <= 0;
    const shouldSendRequestInvite = invitationMode === InvitationMode.Strict && !canManageSubscription;

    const includeTeamStep =
      !(isAgent && isPPS) &&
      !isOneSeatStarter &&
      !isPaymentError &&
      (!isSeatsFullForEnterprise || shouldSendRequestInvite);
    const includeCompanySizeStep = isOwner && !isCompanySizeDetected;
    const includeWebsiteStep = isOwner;

    const steps = prepareOnboardingSteps(
      includeDownloadStep,
      includeInstallStep,
      includeTeamStep,
      includeIntegrationsStep,
      includeWebsiteStep,
      includeCompanySizeStep,
    );

    return steps.map((step, index) => ({
      ...step,
      completed: steps.findIndex((s) => currentRoute.includes(s.route)) >= index,
    }));
  },
);
